import { useEffect, useRef, useState } from "react";

/**
 * Reveals `target` a few characters per frame, easing toward whatever has
 * arrived so far. Streamed text reads smoothly instead of landing in bursts,
 * and a reply that arrives all at once still animates in.
 */
export function useTypewriter(target: string): { text: string; caughtUp: boolean } {
  const [length, setLength] = useState(0);
  const lengthRef = useRef(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (target.length < lengthRef.current) {
      lengthRef.current = 0;
      setLength(0);
    }
    const tick = () => {
      const backlog = target.length - lengthRef.current;
      if (backlog <= 0) {
        frame.current = null;
        return;
      }
      lengthRef.current += Math.max(2, Math.ceil(backlog / 24));
      if (lengthRef.current > target.length) lengthRef.current = target.length;
      setLength(lengthRef.current);
      frame.current = requestAnimationFrame(tick);
    };
    if (frame.current === null) frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [target]);

  return { text: target.slice(0, length), caughtUp: length >= target.length };
}
